import { useEffect, useState } from "react";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { createLegalAwarenessZone } from "services/legalAwarenessService";
import { createLegalTrainingDistributedToZone } from "services/legalTrainingProvision";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "components/ConfirmationModal1";

// Define the Zone type
interface Zone {
    uu_id?: string;
    id?: string | number;
    unit_name?: string;
    name?: string;
    title?: string;
    [key: string]: any;
}

export default function DistributeToZone({ setEdit, formState, type }: { setEdit: any; setFormState: any; formState: any; type?: string }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const { showToast } = useToast();
    const [selectedZones, setSelectedZones] = useState<Zone[]>([]);
    const [zones, setZones] = useState<Zone[]>([]);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [isLoadingZones, setIsLoadingZones] = useState(false);
    const contextData = useAuth();

    useEffect(() => {
        let cancelled = false;
        const adminUnit = contextData?.user?.admin_unit || "";
        const token = contextData.token ?? contextData?.user?.access_token ?? contextData?.user?.user?.access_token;

        if (!adminUnit) return;

        setIsLoadingZones(true);

        fetch(`${process.env.REACT_APP_API_BASE_URL_2}organization/administration-structures/child_units/${adminUnit}/`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
            .then(res => res.json())
            .then((response: any) => {
                if (!cancelled && response) {
                    console.log("API Response:", response); // Debug log
                    
                    let zonesArray: Zone[] = [];
                    
                    // Handle response with message and data/sub_units
                    if (response.sub_units && Array.isArray(response.sub_units)) {
                        zonesArray = response.sub_units;
                    } 
                    else if (response.data && Array.isArray(response.data)) {
                        zonesArray = response.data;
                    }
                    else if (response.units && Array.isArray(response.units)) {
                        zonesArray = response.units;
                    }
                    else if (response.child_admin_units && response.child_admin_units.units && Array.isArray(response.child_admin_units.units)) {
                        zonesArray = response.child_admin_units.units;
                    }
                    else if (response.results && Array.isArray(response.results)) {
                        zonesArray = response.results;
                    }
                    else if (Array.isArray(response)) {
                        zonesArray = response;
                    }
                    else if (response.sub_units === undefined && typeof response === 'object' && response !== null) {
                        // If the response itself is the zones object but not wrapped
                        const possibleZones = Object.values(response).filter(
                            item => item && typeof item === 'object' && (item as any).unit_name
                        );
                        if (possibleZones.length > 0) {
                            zonesArray = possibleZones as Zone[];
                        }
                    }
                    
                    console.log("Extracted zones:", zonesArray); // Debug log
                    setZones(zonesArray);
                    
                    if (zonesArray.length === 0) {
                        console.warn("No zones found in response:", response);
                    }
                }
                setIsLoadingZones(false);
            })
            .catch((error) => {
                if (!cancelled) {
                    console.error("Error fetching zones:", error);
                    showToast(t("FailedToLoadZones") || "Failed to load zones", "error");
                    setZones([]);
                }
                setIsLoadingZones(false);
            });
        return () => { cancelled = true; };
    }, [contextData.token, contextData?.user?.access_token, contextData?.user?.admin_unit, showToast, t]);

    const handleForward = async () => {
        if (selectedZones.length === 0) {
            showToast(t("PleaseSelectAtLeastOneZone") || "Please select at least one zone.", "error");
            return;
        }

        setLoading(true);
        const zonesArray = type === "awareness" ? formState.distrbuted_to_zone : formState.distributed_zones;
        const token = contextData.token ?? contextData?.user?.access_token ?? contextData?.user?.user?.access_token;

        try {
            for (const zone of selectedZones) {
                // Check if already distributed to this zone
                if (zonesArray?.some((z: any) => z.admin_unit_name === zone.unit_name)) {
                    showToast(t("ZoneAlreadyDistributed", { zoneName: zone.unit_name }) || `Zone ${zone.unit_name} has already been distributed to. Skipping.`, "warning");
                    continue;
                }

                const zoneData = new FormData();
                zoneData.append("admin_unit_id", zone.uu_id || String(zone.id || ""));
                zoneData.append("admin_unit_name", zone.unit_name || zone.name || zone.title || "");
                
                let doc;
                let fileurl;
                
                if (type === "awareness") {
                    zoneData.append("status", "Sent To Zonal Office");
                    zoneData.append("admin_unit", zone.uu_id || String(zone.id || ""));
                    zoneData.append("awareness", formState.id);
                    doc = formState.documents?.find((document: any) => document.document_type === "legal_awareness_document_file");
                    
                    if (doc) {
                        fileurl = `${process.env.REACT_APP_API_BASE_URL}legal_awareness/legal-awareness-documents/document/${doc.document_uuid}`;
                        const response = await fetch(fileurl, {
                            headers: {
                                Authorization: `Bearer ${token}`
                            }
                        });
                        const blob = await response.blob();
                        const file = new File([blob], doc.document_path || "file", {
                            type: blob.type,
                        });
                        zoneData.append("awarenes_files", file);
                    }
} else {
                    zoneData.append("status", "sent_to_zonal_office");
                    zoneData.append("training_request", formState.id);
                    doc = formState.documents?.find((document: any) => document.document_type === "training_manual_file");

                    if (doc) {
                        fileurl = `${process.env.REACT_APP_API_BASE_URL}legal_training_provision/legal-training-provision-documents/document/${doc.document_uuid}`;
                        const response = await fetch(fileurl, {
                            headers: {
                                Authorization: `Bearer ${token}`
                            }
                        });
                        const blob = await response.blob();
                        const file = new File([blob], doc.document_path || "file", {
                            type: blob.type,
                        });
                        zoneData.append("training_files", file);
                    }
                }

                zoneData.append("sent_date", new Date().toISOString().split("T")[0]);

                if (type === "awareness") {
                    await createLegalAwarenessZone(zoneData);
                } else {
                    await createLegalTrainingDistributedToZone(zoneData);
                }
            }
            showToast(t("ForwardedToZonesSuccess") || "Forwarded to selected zones successfully!", "success");
            setEdit(false);
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToForwardRequest") || "Failed to update request.", "error");
        } finally {
            setLoading(false);
        }
    };

    const getZoneId = (zone: Zone): string => {
        return zone.uu_id || String(zone.id || "");
    };

    const getZoneName = (zone: Zone): string => {
        return zone.unit_name || zone.name || zone.title || "";
    };

    return (
        <>
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-end p-4 z-50 overflow-hidden">
                {/* Distribute to Zone Modal */}
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-2xl w-full max-w-lg p-6 relative flex flex-col h-full my-auto overflow-y-auto pr-2 custom-scrollbar border border-gray-200">
                    <button
                        onClick={() => setEdit(false)}
                        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label={t("Close") || "Close"}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>

                    <div className="flex items-center mb-6">
                        <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800">
                            {t("DistributeToZone") || "Distribute To Zone"}
                        </h2>
                    </div>
                    
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("SelectZones") || "Select Zones"}
                    </label>
                    <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-3 mb-4 bg-gray-50">
                        {isLoadingZones ? (
                            <div className="flex justify-center py-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                                <span className="ml-2 text-gray-600">{t("LoadingZones") || "Loading zones..."}</span>
                            </div>
                        ) : zones.length === 0 ? (
                            <p className="text-gray-500 text-sm italic text-center py-4">
                                {t("NoZonesAvailable") || "No zones available"}
                            </p>
                        ) : (
                            zones.map((zone: Zone, index: number) => {
                                const zoneId = getZoneId(zone) || `zone-${index}`;
                                const zoneName = getZoneName(zone);
                                return (
                                    <div key={zoneId} className="flex items-center mb-2 p-2 rounded hover:bg-white transition-colors">
                                        <input
                                            type="checkbox"
                                            id={zoneId}
                                            checked={selectedZones.some((z) => getZoneId(z) === zoneId)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedZones([...selectedZones, zone]);
                                                } else {
                                                    setSelectedZones(selectedZones.filter((z) => getZoneId(z) !== zoneId));
                                                }
                                            }}
                                            className="mr-3 w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                                        />
                                        <label htmlFor={zoneId} className="text-sm cursor-pointer text-gray-700 font-medium">
                                            {zoneName}
                                        </label>
                                    </div>
                                );
                            })
                        )}
                    </div>
                    
                    {selectedZones.length > 0 && (
                        <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-sm font-semibold text-gray-800 mb-2">
                                {t("SelectedZonesCount", { count: selectedZones.length }) || `Selected Zones (${selectedZones.length}):`}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {selectedZones.map((zone, index) => {
                                    const zoneId = getZoneId(zone) || `selected-${index}`;
                                    const zoneName = getZoneName(zone);
                                    return (
                                        <span key={zoneId} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800 border border-green-300 shadow-sm">
                                            {zoneName}
                                            <button
                                                onClick={() => setSelectedZones(selectedZones.filter((z) => getZoneId(z) !== zoneId))}
                                                className="ml-2 text-green-600 hover:text-green-800 font-bold text-lg leading-none"
                                                aria-label={t("RemoveZone") || "Remove zone"}
                                            >
                                                ×
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    
                    <div className="mt-6 pb-8 flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
                        <button
                            onClick={() => setEdit(false)}
                            className="flex-1 px-6 py-3 rounded-lg bg-gray-200 text-gray-800 font-semibold hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-colors"
                        >
                            {t("Cancel") || "Cancel"}
                        </button>
                        <button
                            onClick={() => setIsConfirmationModalOpen(true)}
                            disabled={selectedZones.length === 0 || loading}
                            className="flex-1 px-6 py-3 rounded-lg bg-gradient-to-r from-gray-800 to-black text-white font-semibold hover:from-gray-900 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                        >
                            {loading ? (
                                <div className="flex items-center justify-center">
                                    <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {t("Forwarding") || "Forwarding..."}
                                </div>
                            ) : (
                                t("ForwardToZones", { count: selectedZones.length }) || `Forward To Zones (${selectedZones.length})`
                            )}
                        </button>
                    </div>
                </div>
            </div>
            
            <ConfirmationModal
                isOpen={isConfirmationModalOpen}
                isLoading={loading}
                onConfirm={() => {
                    handleForward();
                    setIsConfirmationModalOpen(false);
                }}
                onCancel={() => setIsConfirmationModalOpen(false)}
                title={t("ConfirmDistribution") || "Confirm Distribution"}
                message={t("ConfirmDistributionMessage", { count: selectedZones.length }) || `Are you sure you want to distribute to ${selectedZones.length} selected zone(s)? This action will forward the request to the zonal offices.`}
                confirmText={t("Distribute") || "Distribute"}
                cancelText={t("Cancel") || "Cancel"}
                type="warning"
            />
        </>
    );
}